import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../../ui/Dropdown';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { useLanguage } from '../../../localization/useLanguage';
import { CHAT_TYPE_GROUP, MUTE_STATUS } from '../../../Helpers/Chat/Constant';
import { getChatMessageHistoryById } from '../../../Helpers/Chat/ChatHelper';
import { formatGroupIdToJid, formatUserIdToJid, getUserDetails, isLocalUser } from '../../../Helpers/Chat/User';
import {
    IconArchive,
    IconUnarchiveLine,
    IconBell,
    IconBellOff,
    IconMarkRead,
    IconMarkUnread,
    IconMenuEdit,
    IconLockClosed,
    IconStar,
    IconUnstar,
    IconBroom,
    IconMenuDelete,
    IconBlock,
    IconLogOut,
    IconFlag,
    IconPin,
    IconUnpin,
} from '../../../Components/Common/Icons';
import { CHAT_MENU_ACTION, isChatMenuActionAvailable } from '../../../Helpers/Chat/chatMenuCapability';
import EditChatDialog from '../../../Components/WebChat/Common/EditChatDialog';
import useChatActions from './useChatActions';
import MuteDurationDialog from './MuteDurationDialog';
import styles from './index.module.scss';

const A = CHAT_MENU_ACTION;

export default function RecentChatContextMenu({
    open,
    onOpenChange,
    position,
    chatId,
    chatType,
    archiveStatus,
    muteStatus,
    pinStatus,
    favouriteStatus,
    unreadFlag,
}) {
    const { t } = useLanguage();
    const actions = useChatActions();
    const [confirm, setConfirm] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [muteOpen, setMuteOpen] = useState(false);
    const {
        isDeleteChatEnabled = true,
        isClearChatEnabled = true,
        isBlockEnabled = true,
        isReportEnabled = true,
    } = useSelector(state => state.featureStateData);
    const blockedContacts = useSelector(state => state.blockedContact?.data) || [];
    const isBlocked = blockedContacts.includes(formatUserIdToJid(chatId));
    const groupParticipants = useSelector(state => state.groupsMemberParticipantsListData?.groupParticipants);
    const groups = useSelector(state => state.groupsData?.data);

    const isGroup = chatType === CHAT_TYPE_GROUP;
    // Exact match rather than getGroupData(), whose substring compare is meant for JIDs.
    const group = isGroup ? groups?.find(item => item.groupId === chatId) : null;
    // Only a current member can leave; for anyone already removed the item is pointless.
    const membership = isGroup
        ? groupParticipants?.[formatGroupIdToJid(chatId)]?.find(user => isLocalUser(user.userId))
        : null;
    const isArchived = archiveStatus === 1;
    const isMuted = muteStatus === MUTE_STATUS.MUTED;
    // Named in the mute prompt: the group name, or the other participant's display name.
    const chatName = isGroup
        ? group?.groupName || ''
        : getUserDetails(formatUserIdToJid(chatId))?.displayName || chatId;
    const isPinned = pinStatus === 1;
    const isFavourite = favouriteStatus === 1;
    const isUnread = unreadFlag === 1;

    // Report ships recent messages, which load only after the chat is opened.
    // Latched while the menu opens: the confirm dialog outlives the menu and
    // would otherwise lose its checkbox once `open` flips back to false.
    const [hasHistory, setHasHistory] = useState(false);
    useEffect(() => {
        if (open) setHasHistory(getChatMessageHistoryById(chatId).length > 0);
    }, [open, chatId]);

    const run = fn => () => fn();

    const items = [
        {
            key: A.ARCHIVE,
            label: isArchived ? t('chat.menu.UNARCHIVE_CHAT') : t('chat.menu.ARCHIVE_CHAT'),
            icon: isArchived ? <IconUnarchiveLine /> : <IconArchive />,
            onSelect: run(() => actions.archiveChat(chatId, chatType, !isArchived)),
        },
        {
            key: A.PIN,
            label: isPinned ? t('chat.menu.UNPIN_CHAT') : t('chat.menu.PIN_CHAT'),
            icon: isPinned ? <IconUnpin /> : <IconPin />,
            onSelect: run(() => actions.pinChat(chatId, chatType, !isPinned)),
        },
        {
            key: A.MUTE,
            label: isMuted ? t('chat.menu.UNMUTE_CHAT') : t('chat.menu.MUTE_CHAT'),
            icon: isMuted ? <IconBell /> : <IconBellOff />,
            onSelect: run(() => (isMuted ? actions.muteChat(chatId, chatType, false) : setMuteOpen(true))),
        },
        {
            key: A.MARK_READ,
            label: isUnread ? t('chat.menu.MARK_AS_READ') : t('chat.menu.MARK_AS_UNREAD'),
            icon: isUnread ? <IconMarkRead /> : <IconMarkUnread />,
            onSelect: run(() => actions.markChatUnread(chatId, chatType, !isUnread)),
        },
        { separator: true },
        ...(isGroup
            ? [
                  {
                      key: A.EDIT,
                      label: t('chat.menu.EDIT_CHAT'),
                      icon: <IconMenuEdit />,
                      // Only a member can edit, matching the editor in group info.
                      // An admin-blocked group rejects profile writes outright.
                      hidden: !membership || group?.isAdminBlocked,
                      onSelect: () => setEditOpen(true),
                  },
              ]
            : []),
        {
            key: A.LOCK,
            label: t('chat.menu.LOCK_CHAT'),
            icon: <IconLockClosed />,
        },
        {
            key: A.FAVORITE,
            label: isFavourite ? t('chat.menu.REMOVE_FROM_FAVORITES') : t('chat.menu.ADD_TO_FAVORITES'),
            icon: isFavourite ? <IconUnstar /> : <IconStar />,
            onSelect: run(() => actions.favouriteChat(chatId, chatType, !isFavourite)),
        },
        {
            key: A.CLEAR,
            label: t('chat.menu.CLEAR_CHAT'),
            icon: <IconBroom />,
            hidden: !isClearChatEnabled,
            onSelect: () =>
                setConfirm({
                    title: t('chat.menu.CLEAR_CHAT'),
                    description: t('chat.menu.CLEAR_CHAT_CONFIRM_DESC'),
                    confirmLabel: t('chat.CLEAR'),
                    variant: 'destructive',
                    onConfirm: () => actions.clearChat(chatId, chatType),
                }),
        },
        { separator: true },
        {
            key: A.DELETE,
            label: t('chat.menu.DELETE_CHAT'),
            icon: <IconMenuDelete />,
            danger: true,
            hidden: !isDeleteChatEnabled,
            onSelect: () =>
                setConfirm({
                    title: t('chat.menu.DELETE_CHAT'),
                    description: t('chat.menu.DELETE_CHAT_CONFIRM_DESC'),
                    confirmLabel: t('chat.DELETE'),
                    variant: 'destructive',
                    onConfirm: () => actions.deleteChat(chatId, chatType),
                }),
        },
        {
            key: A.BLOCK,
            label: isBlocked ? t('chat.menu.UNBLOCK_CHAT') : t('chat.menu.BLOCK_CHAT'),
            icon: <IconBlock />,
            danger: true,
            hidden: !isBlockEnabled || isGroup,
            onSelect: () =>
                isBlocked
                    ? actions.blockChat(chatId, true)
                    : setConfirm({
                          title: t('chat.BLOCK_CONTACT'),
                          description: t('chat.BLOCK_DESC'),
                          confirmLabel: t('chat.BLOCK'),
                          variant: 'destructive',
                          // Offered together so the user need not reopen the menu.
                          checkboxLabel: isReportEnabled && hasHistory ? t('chat.menu.BLOCK_ALSO_REPORT') : undefined,
                          checkboxSub: t('chat.menu.BLOCK_ALSO_REPORT_SUB'),
                          onConfirm: async alsoReport => {
                              await actions.blockChat(chatId, false);
                              if (alsoReport) await actions.reportChat(chatId, chatType);
                          },
                      }),
        },
        {
            key: A.LEAVE_GROUP,
            label: t('chat.menu.LEAVE_GROUP'),
            icon: <IconLogOut />,
            danger: true,
            hidden: !membership,
            onSelect: () =>
                setConfirm({
                    title: t('chat.menu.LEAVE_GROUP'),
                    description: t('chat.menu.LEAVE_GROUP_CONFIRM'),
                    confirmLabel: t('chat.menu.LEAVE_AND_DELETE'),
                    secondaryLabel: t('chat.menu.LEAVE_GROUP'),
                    variant: 'destructive',
                    onSecondary: () => actions.leaveGroup(chatId, membership.userJid, false),
                    onConfirm: () => actions.leaveGroup(chatId, membership.userJid, true),
                }),
        },
        {
            key: A.REPORT,
            label: t('chat.menu.REPORT_CHAT'),
            icon: <IconFlag />,
            danger: true,
            hidden: !isReportEnabled,
            disabledReason: hasHistory ? null : t('chat.menu.NO_MESSAGES_TO_REPORT'),
            onSelect: () =>
                setConfirm({
                    title: isGroup ? t('chat.REPORT_GROUP') : t('chat.REPORT_CHAT'),
                    description: isGroup ? t('chat.REPORT_DESC_GROUP') : t('chat.REPORT_DESC_CONTACT'),
                    confirmLabel: t('chat.REPORT'),
                    variant: 'destructive',
                    checkboxLabel: !isGroup && isBlockEnabled ? t('chat.menu.REPORT_ALSO_BLOCK') : undefined,
                    checkboxSub: t('chat.menu.REPORT_ALSO_BLOCK_SUB'),
                    onConfirm: async alsoBlock => {
                        await actions.reportChat(chatId, chatType);
                        if (alsoBlock) await actions.blockChat(chatId, false);
                    },
                }),
        },
    ];

    return (
        <>
            <DropdownMenu open={open} onOpenChange={onOpenChange}>
                {/* Anchored to the pointer, so the trigger is a zero-size marker rather than a control. */}
                <DropdownMenuTrigger
                    aria-hidden
                    tabIndex={-1}
                    className={styles.anchor}
                    style={{ left: position.x, top: position.y }}
                />
                <DropdownMenuContent align="start" sideOffset={0} className={styles.menu}>
                    {items.map((item, index) => {
                        if (item.separator) return <DropdownMenuSeparator key={`sep-${index}`} />;
                        if (item.hidden) return null;

                        const unavailable = !isChatMenuActionAvailable(item.key);
                        const disabled = unavailable || Boolean(item.disabledReason);
                        const title = unavailable ? t('chat.menu.COMING_SOON') : item.disabledReason || undefined;

                        return (
                            <DropdownMenuItem
                                key={item.key}
                                disabled={disabled}
                                title={title}
                                className={item.danger ? styles.danger : undefined}
                                onSelect={disabled ? undefined : item.onSelect}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </DropdownMenuItem>
                        );
                    })}
                </DropdownMenuContent>
            </DropdownMenu>

            {confirm && (
                <ConfirmDialog
                    open
                    onOpenChange={isOpen => !isOpen && setConfirm(null)}
                    cancelLabel={t('common.CANCEL')}
                    {...confirm}
                />
            )}

            {/* Mounted only while open so the fields reseed from the store each time. */}
            {editOpen && (
                <EditChatDialog
                    open
                    onOpenChange={setEditOpen}
                    groupJid={formatGroupIdToJid(chatId)}
                    initialName={group?.groupName || ''}
                    onSave={payload => actions.editChatProfile(chatId, payload)}
                />
            )}

            {muteOpen && (
                <MuteDurationDialog
                    open
                    onOpenChange={setMuteOpen}
                    chatName={chatName}
                    onPick={duration => actions.muteChat(chatId, chatType, true, duration)}
                />
            )}
        </>
    );
}
